'use client';

import { useState, useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import moment from 'moment';
import type { Moment } from 'moment';
import Chip from '@mui/material/Chip';
import TodayIcon from '@mui/icons-material/Today';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { useQuery } from '@apollo/client/react';
import { cityCalendarEventDatesQuery } from '@/apollo/cities';
import { filterableRoute } from '@/utils/routes';
import ServerDay from '@/components/calendar/server-day';

type Props = {
  cityId: string;
};

const JumpToDateChip = ({ cityId }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState<Moment | null>(null);

  const startParam = searchParams.get('start');

  const label = startParam
    ? moment(startParam).format('MMM D')
    : 'Jump to date';

  const [displayedMonth, setDisplayedMonth] = useState<Moment>(
    () => (startParam ? moment(startParam).startOf('month') : moment().startOf('month')),
  );

  const monthStart = displayedMonth.clone().startOf('month').toISOString();
  const monthEnd = displayedMonth.clone().endOf('month').toISOString();

  const { data } = useQuery(cityCalendarEventDatesQuery, {
    variables: { id: cityId, start: monthStart, end: monthEnd },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });

  const highlightedDays = useMemo(() => {
    if (!data?.city?.calendarEventDates) return [];
    return data.city.calendarEventDates.map((d: string) => moment.utc(d).date());
  }, [data]);

  const handleMonthChange = useCallback((date: Moment) => {
    setDisplayedMonth(date.clone().startOf('month'));
  }, []);

  const handleOpen = useCallback(() => {
    setPickerValue(startParam ? moment(startParam) : moment());
    setOpen(true);
  }, [startParam]);

  const handleChange = useCallback((date: Moment | null) => {
    setPickerValue(date);
  }, []);

  const handleAccept = useCallback(() => {
    const date = pickerValue ?? moment();

    const params: Record<string, string | null> = {};
    searchParams.forEach((v, k) => { params[k] = v; });

    const isToday = date.isSame(moment(), 'day');

    if (isToday) {
      delete params.start;
      delete params.page;
    } else {
      params.start = date.format('YYYY-MM-DD');
      delete params.page;
    }

    const { filter, type, categoryIds, start } = params;
    router.push(filterableRoute('/calendar', { filter, type, categoryIds, start }));
  }, [pickerValue, router, searchParams]);

  return (
    <>
      <Chip
        icon={<TodayIcon />}
        label={label}
        onClick={handleOpen}
        color={startParam ? 'primary' : 'default'}
        variant={startParam ? 'filled' : 'outlined'}
        sx={{ flexShrink: 0 }}
      />
      <MobileDatePicker
        open={open}
        onClose={() => setOpen(false)}
        onAccept={handleAccept}
        onChange={handleChange}
        onMonthChange={handleMonthChange}
        value={pickerValue}
        slots={{ day: ServerDay }}
        slotProps={{
          day: { highlightedDays } as Record<string, unknown>,
          textField: { sx: { display: 'none' } },
        }}
      />
    </>
  );
};

export default JumpToDateChip;
